import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useFormik } from "formik";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { showToast } from "@/redux/actions/toast-actions";
import { approveApproval, rejectApproval } from "@/services/approvals.service";
import { approvalActionSchema } from "@/schemas/approval.schema";
import { formatApprovalLabel, getModuleQueryKeys } from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 440,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme, action }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor:
    action === "approve"
      ? theme.palette.success.main + "18"
      : theme.palette.error.main + "18",
}));

export const ApprovalActionDialog = ({ open, onClose, item, action }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isApprove = action === "approve";

  const formik = useFormik({
    initialValues: { note: "" },
    validationSchema: approvalActionSchema,
    onSubmit: (values) => {
      mutation.mutate({
        module: item.module,
        id: item.id,
        note: values.note || undefined,
      });
    },
  });

  const closeAndReset = () => {
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: isApprove ? approveApproval : rejectApproval,
    onSuccess: (data) => {
      dispatch(
        showToast(
          data?.message ?? `Request ${isApprove ? "approved" : "rejected"}.`,
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["approvals"] });
      queryClient.invalidateQueries({ queryKey: ["approvalStats"] });
      queryClient.invalidateQueries({
        queryKey: ["approval", item.module, item.id],
      });
      // The underlying resource (e.g. a report) is owned by its own module
      // and cached under that module's own query keys - refresh those too.
      getModuleQueryKeys(item.module).forEach((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      );
      // Always close on success - don't gate this on mutation.isPending like
      // the Cancel button does. The closure captured here still reads the
      // in-flight (pending) mutation state, so that guard would block this.
      closeAndReset();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    closeAndReset();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2, px: 2, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper action={action}>
            {isApprove ? (
              <CheckCircleOutlineIcon
                sx={{ color: "success.main", fontSize: 22 }}
              />
            ) : (
              <HighlightOffIcon sx={{ color: "error.main", fontSize: 22 }} />
            )}
          </IconWrapper>
          <Box>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              {isApprove ? "Approve Request" : "Reject Request"}
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              {isApprove ? "Approve" : "Reject"}{" "}
              <strong>{formatApprovalLabel(item?.action)}</strong>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <FormTextInput
          formik={formik}
          name="note"
          label="Note (optional)"
          placeholder={
            isApprove
              ? "Add an approval note"
              : "Explain why this is being rejected"
          }
        />

        {mutation.isError && (
          <Box sx={{ mt: 2 }}>
            <CustomErrorMessage
              message={
                mutation.error?.message ?? "Something went wrong. Please try again."
              }
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomButton
          variantType={isApprove ? "default" : "destructive"}
          size="small"
          onClick={formik.handleSubmit}
          disabled={mutation.isPending}
          sx={{ minWidth: 100 }}
        >
          {mutation.isPending ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : isApprove ? (
            "Approve"
          ) : (
            "Reject"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ApprovalActionDialog;
